import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { SignOut, Coin, Waveform, Plus, Tray, Warning, Code } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { apiClient, API } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SoundGenerator from "@/components/SoundGenerator";
import MusicGenerator from "@/components/MusicGenerator";
import VoiceGenerator from "@/components/VoiceGenerator";
import VideoFx from "@/components/VideoFx";
import SoundLibrary from "@/components/SoundLibrary";
import Mixer from "@/components/Mixer";
import PricingModal from "@/components/PricingModal";
import OnboardingShowcase from "@/components/OnboardingShowcase";
import SupportWidget from "@/components/SupportWidget";
import SupportInbox from "@/components/SupportInbox";
import ApiKeysModal from "@/components/ApiKeysModal";

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, logout, refresh } = useAuth();
  const { t } = useLang();
  const [genTab, setGenTab] = useState("sfx");
  const [sounds, setSounds] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [mixerTracks, setMixerTracks] = useState([]);
  const [loadingLib, setLoadingLib] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [showPromo, setShowPromo] = useState(() => !localStorage.getItem("has_seen_promo"));
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [apiOpen, setApiOpen] = useState(false);
  const [engine, setEngine] = useState({ available: true, reason: null });

  const checkEngine = useCallback(() => {
    apiClient.get("/engine/status")
      .then(({ data }) => setEngine(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    checkEngine();
    const handler = () => checkEngine();
    window.addEventListener("aurafx:engine-check", handler);
    return () => window.removeEventListener("aurafx:engine-check", handler);
  }, [checkEngine]);

  useEffect(() => {
    apiClient.post("/payments/reconcile").then(({ data }) => {
      if (data.credits_added > 0) {
        toast.success(`+${data.credits_added} ${t("creditsShort")}`);
        refresh();
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.is_admin) return;
    const fetchUnread = () =>
      apiClient.get("/support/admin/unread_count").then(({ data }) => setInboxUnread(data.unread)).catch(() => {});
    fetchUnread();
    const iv = setInterval(fetchUnread, 60000);
    return () => clearInterval(iv);
  }, [user?.is_admin]);

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    const handler = () => setPricingOpen(true);
    window.addEventListener("aurafx:out-of-credits", handler);
    return () => window.removeEventListener("aurafx:out-of-credits", handler);
  }, []);

  // Handle return from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("stripe_session_id");
    const cancel = params.get("stripe_cancel");
    if (cancel) {
      toast.info("Payment cancelled");
      window.history.replaceState(null, "", "/dashboard");
      return;
    }
    if (!sessionId || !user) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled || attempts >= 6) return;
      attempts++;
      try {
        const { data } = await apiClient.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success(`+${data.credits} credits added!`);
          window.history.replaceState(null, "", "/dashboard");
          refresh();
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment expired");
          window.history.replaceState(null, "", "/dashboard");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [location.search, user, refresh]);

  const loadSounds = useCallback(async () => {
    if (!user) return;
    setLoadingLib(true);
    try {
      const params = {};
      if (activeCategory) params.category = activeCategory;
      if (favoritesOnly) params.favorite_only = true;
      const { data } = await apiClient.get("/sounds", { params });
      setSounds(data);
    } catch (e) {
      toast.error("Failed to load library");
    } finally {
      setLoadingLib(false);
    }
  }, [user, activeCategory, favoritesOnly]);

  useEffect(() => {
    loadSounds();
  }, [loadSounds]);

  const handleGenerated = (sound) => {
    setSounds((s) => [sound, ...s]);
    refresh();
    if (!engine.available) checkEngine();
  };

  const toggleFavorite = async (sound_id) => {
    try {
      const { data } = await apiClient.patch(`/sounds/${sound_id}/favorite`);
      setSounds((arr) => arr.map((s) => (s.sound_id === sound_id ? { ...s, favorite: data.favorite } : s)));
    } catch {
      toast.error("Failed to update favorite");
    }
  };

  const deleteSound = async (sound_id) => {
    try {
      await apiClient.delete(`/sounds/${sound_id}`);
      setSounds((arr) => arr.filter((s) => s.sound_id !== sound_id));
      setMixerTracks((arr) => arr.filter((t) => t.sound.sound_id !== sound_id));
      toast.success("Sound deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const downloadSound = (sound, fmt) => {
    const link = document.createElement("a");
    if (sound.audio_b64) {
      const ext = (sound.audio_mime || "audio/mpeg") === "audio/wav" ? "wav" : "mp3";
      const tag = (sound.category || "sfx").toLowerCase();
      const slug = (sound.prompt || "").toLowerCase().match(/[a-z0-9]+/g)?.slice(0, 4).join("-") || "sound";
      link.href = `data:${sound.audio_mime || "audio/mpeg"};base64,${sound.audio_b64}`;
      link.download = sound.is_mix ? `${tag.toUpperCase()}_${slug}_mix.mp3` : `aurafx-${tag}-${slug}.${ext}`;
    } else {
      link.href = `${API}/sounds/${sound.sound_id}/download`;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const addToMixer = (sound) => {
    if (mixerTracks.some((t) => t.sound.sound_id === sound.sound_id)) {
      toast.info("Already in mixer");
      return;
    }
    if (mixerTracks.length >= 6) {
      toast.warning("Mixer limit: 6 tracks");
      return;
    }
    setMixerTracks((arr) => [...arr, { sound, volume: 0.8, muted: false, solo: false, delay: 0, fadeIn: 0, fadeOut: 0, pan: 0, loop: false }]);
    toast.success(`Added to mixer`);
  };

  const removeFromMixer = (sound_id) => {
    setMixerTracks((arr) => arr.filter((t) => t.sound.sound_id !== sound_id));
  };

  const updateTrack = (sound_id, patch) => {
    setMixerTracks((arr) => arr.map((t) => (t.sound.sound_id === sound_id ? { ...t, ...patch } : t)));
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="w-10 h-10 border-2 border-white/20 border-t-[color:var(--accent)] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="dashboard">
      {/* Header */}
      <header className="border-b border-white/5 bg-[color:var(--bg)]/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[color:var(--accent)] grid place-items-center">
              <Waveform size={18} weight="fill" color="white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">AuraFX</span>
            <span className="ml-3 text-xs font-mono-num uppercase tracking-widest text-white/40 hidden sm:inline">{t("studio")}</span>
          </div>
          <div className="flex items-center gap-4">
            <div
              data-testid="credits-pill"
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10"
              title="Credits left"
            >
              <Coin size={16} weight="fill" className="text-[color:var(--warning)]" />
              <span className="font-mono-num text-sm">{user.credits}</span>
              <span className="text-xs text-white/40 uppercase tracking-widest">{t("credits")}</span>
            </div>
            <button
              data-testid="buy-credits-btn"
              onClick={() => setPricingOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white text-sm font-medium transition-colors"
            >
              <Plus size={14} weight="bold" /> <span className="hidden sm:inline">{t("buyCredits")}</span>
            </button>
            <LanguageSwitcher />
            <button
              data-testid="api-keys-btn"
              onClick={() => setApiOpen(true)}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-white/70 hover:text-white hover:border-white/25 text-sm font-medium transition-colors"
              title={t("apiTitle")}
            >
              <Code size={15} weight="bold" /> {t("apiNav")}
            </button>
            {user.is_admin && (
              <button
                data-testid="support-inbox-btn"
                onClick={() => { setInboxOpen(true); setInboxUnread(0); }}
                className="relative p-2 rounded-lg hover:bg-white/5 transition-colors"
                title={t("inbTitle")}
              >
                <Tray size={18} className="text-white/60" />
                {inboxUnread > 0 && (
                  <span data-testid="inbox-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[color:var(--accent)] text-[10px] font-mono-num grid place-items-center text-white">
                    {inboxUnread}
                  </span>
                )}
              </button>
            )}
            <div className="flex items-center gap-2.5">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/10" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-white/10 grid place-items-center text-xs">{user.name?.[0]}</div>
              )}
              <span className="hidden md:inline text-sm text-white/80 max-w-[140px] truncate">{user.name}</span>
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="p-2 rounded-lg hover:bg-white/5 transition-colors"
              title={t("logout")}
            >
              <SignOut size={18} className="text-white/60" />
            </button>
          </div>
        </div>
      </header>

      {!engine.available && (
        <div
          data-testid="engine-down-banner"
          className="bg-[color:var(--warning)]/10 border-b border-[color:var(--warning)]/30"
        >
          <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-start gap-3">
            <Warning size={20} weight="fill" className="text-[color:var(--warning)] shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-white">{t("engineDownTitle")}</p>
              <p className="text-xs text-white/60 mt-0.5 break-words">
                {engine.reason || t("engineDownDefault")}
              </p>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-[1600px] mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-6">
          <div>
            <div className="flex gap-1.5 mb-4" data-testid="generator-tabs">
              {[
                { id: "sfx", label: t("sfxTab") },
                { id: "music", label: t("musicTab") },
                { id: "voice", label: t("voiceTab") },
                { id: "video", label: t("videoTab") },
              ].map((tab) => (
                <button
                  key={tab.id}
                  data-testid={`gen-tab-${tab.id}`}
                  onClick={() => setGenTab(tab.id)}
                  className={`text-xs px-4 py-2 rounded-full font-mono-num uppercase tracking-widest border transition-colors ${
                    genTab === tab.id
                      ? "bg-[color:var(--accent)] border-[color:var(--accent)] text-white shadow-[0_0_14px_-3px_rgba(229,9,20,0.7)]"
                      : "border-white/10 text-white/50 hover:text-white hover:border-white/25"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {genTab === "sfx" && <SoundGenerator onGenerated={handleGenerated} credits={user.credits} />}
            {genTab === "music" && (
              <section className="rounded-2xl border border-white/10 bg-[color:var(--surface)] p-6" data-testid="generator-panel-music">
                <p className="text-xs font-mono-num uppercase tracking-widest text-white/40">{t("musicTab")}</p>
                <h2 className="font-display font-semibold text-xl tracking-tight mt-0.5 mb-4">{t("musicTitle")}</h2>
                <MusicGenerator onGenerated={handleGenerated} credits={user.credits} />
              </section>
            )}
            {genTab === "voice" && (
              <section className="rounded-2xl border border-white/10 bg-[color:var(--surface)] p-6" data-testid="generator-panel-voice">
                <p className="text-xs font-mono-num uppercase tracking-widest text-white/40">{t("voiceTab")}</p>
                <h2 className="font-display font-semibold text-xl tracking-tight mt-0.5 mb-4">{t("voiceTitle")}</h2>
                <VoiceGenerator onGenerated={handleGenerated} credits={user.credits} />
              </section>
            )}
            {genTab === "video" && (
              <section className="rounded-2xl border border-white/10 bg-[color:var(--surface)] p-6" data-testid="generator-panel-video">
                <p className="text-xs font-mono-num uppercase tracking-widest text-white/40">{t("videoTab")}</p>
                <h2 className="font-display font-semibold text-xl tracking-tight mt-0.5 mb-4">{t("vfxTitle")}</h2>
                <VideoFx credits={user.credits} onCreditsSpent={refresh} />
              </section>
            )}
          </div>
          <Mixer
            tracks={mixerTracks}
            onUpdateTrack={updateTrack}
            onRemoveTrack={removeFromMixer}
            onExported={(snd) => setSounds((prev) => [snd, ...prev])}
          />
        </div>
        <div className="lg:col-span-5">
          <SoundLibrary
            sounds={sounds}
            loading={loadingLib}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            favoritesOnly={favoritesOnly}
            setFavoritesOnly={setFavoritesOnly}
            onToggleFavorite={toggleFavorite}
            onDelete={deleteSound}
            onDownload={downloadSound}
            onAddToMixer={addToMixer}
            onRefined={handleGenerated}
          />
        </div>
      </main>
      <PricingModal open={pricingOpen} onClose={() => setPricingOpen(false)} />
      {showPromo && <OnboardingShowcase onClose={() => setShowPromo(false)} />}
      <SupportWidget />
      {inboxOpen && <SupportInbox onClose={() => setInboxOpen(false)} />}
      {apiOpen && <ApiKeysModal onClose={() => setApiOpen(false)} />}
    </div>
  );
}
